import React from 'react';
import { Radio as MuiRadio, RadioProps as MuiRadioProps, styled, radioClasses, alpha, useTheme } from '@mui/material';

const StyledRadio = styled(MuiRadio)(({ theme }) => ({
  [`&.${radioClasses.root}`]: {
    padding: '4px',
    '&.MuiRadio-sizeSmall': {
      padding: '3px',
    },
    '&:hover': {
      backgroundColor: '#D5D7DA',
      '& .unchecked-icon': {
        fill: 'white',
      },
    },
    '&.Mui-focusVisible': {
      backgroundColor: alpha(theme.palette.primary.main, 0.3),
    },
  },
}));

// Simple radio icon component
const RadioIcon: React.FC<{ size?: 'small' | 'medium' | 'large'; disabled?: boolean }> = ({
  size = 'medium',
  disabled = false,
}) => {
  const theme = useTheme();
  const iconSize = size === 'small' ? 16 : size === 'medium' ? 20 : 24;
  const strokeColor = disabled ? theme.custom?.colours?.grey300 || '#ccc' : '#D0D5DD';
  const fillColor = disabled ? theme.custom?.colours?.grey100 || '#f5f5f5' : '#fff';

  return (
    <svg
      className="unchecked-icon"
      width={iconSize}
      height={iconSize}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="12" r="10" stroke={strokeColor} strokeWidth="2" fill={fillColor} />
    </svg>
  );
};

// Checked radio icon component
const RadioCheckedIcon: React.FC<{ size?: 'small' | 'medium' | 'large'; disabled?: boolean }> = ({
  size = 'medium',
  disabled = false,
}) => {
  const theme = useTheme();
  const iconSize = size === 'small' ? 16 : size === 'medium' ? 20 : 24;
  const strokeColor = disabled ? theme.custom?.colours?.grey300 || '#ccc' : theme.palette.primary.main;
  const fillColor = disabled ? theme.custom?.colours?.grey100 || '#f5f5f5' : theme.palette.primary.main;

  return (
    <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke={strokeColor} strokeWidth="2" fill={fillColor} />
      <circle cx="12" cy="12" r="4" fill="#fff" />
    </svg>
  );
};

type RadioProps = Pick<MuiRadioProps, 'size' | 'disabled' | 'checked' | 'onChange' | 'slotProps' | 'sx' | 'readOnly'>;

export const Radio: React.FC<RadioProps> = (props) => {
  return (
    <StyledRadio
      {...props}
      focusRipple={false}
      checkedIcon={<RadioCheckedIcon size={props.size} disabled={props.disabled} />}
      icon={<RadioIcon size={props.size} disabled={props.disabled} />}
    />
  );
};
